import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'components/Button/Button'
import { EMSN, EMSNSortForm } from 'models/eMSN'
import { EMSNCard } from '..'
import { useMedicareUrl } from 'hooks/useMedicareUrl'
import { useDispatch } from 'react-redux'
import { logAriaMessage } from 'redux/reducers/aria-log'

export interface EMSNDisplayProps {
  eMSNs: EMSN[]
  sortCriteria: EMSNSortForm
}
const defaultEMSNCountToShow = 10
const defaultEMSNCountToIncrease = defaultEMSNCountToShow

export const EMSNDisplay: FC<EMSNDisplayProps> = ({ eMSNs, sortCriteria }) => {
  const { t } = useTranslation('eMSNPage')
  const dispatch = useDispatch()
  const resultsHeading = useRef<HTMLHeadingElement | null>(null)

  const searchClaimsUrl = useMedicareUrl(
    'https://www.medicare.gov/mbp/claimsearchresult.aspx?Lang=en-us',
    'https://www.medicare.gov/mbp/claimsearchresult.aspx?Lang=es-mx'
  )
  const sortFunction = (a: EMSN, b: EMSN): number => {
    if (sortCriteria.sortBy === 'eMSNDate') {
      const aDate = new Date(a.date).getTime()
      const bDate = new Date(b.date).getTime()
      if (sortCriteria.sortOrder === 'ascending') {
        return aDate - bDate
      } else {
        return bDate - aDate
      }
    }

    // Sort by claim type
    const aClaimType = a.claimType.toLowerCase()
    const bClaimType = b.claimType.toLowerCase()
    if (aClaimType === bClaimType) {
      return 0
    }
    if (sortCriteria.sortOrder === 'ascending') {
      return (aClaimType < bClaimType) ? -1 : 1
    } else {
      return (aClaimType > bClaimType) ? -1 : 1
    }
  }

  const [eMSNsToShow, setEMSNsToShow] = useState<number>(
    Math.min(eMSNs.length, defaultEMSNCountToShow)
  )
  useEffect(() => {
    setEMSNsToShow(Math.min(eMSNs.length, defaultEMSNCountToShow))
  }, [eMSNs.length, setEMSNsToShow])

  const eMSNShownCountRef = useRef<HTMLParagraphElement>(null)
  const loadMoreEMSNs = useCallback(() => {
    setEMSNsToShow(Math.min(eMSNsToShow + defaultEMSNCountToIncrease, eMSNs.length))
    resultsHeading.current?.focus()
    dispatch(logAriaMessage(t('emsn-search.search-complete')))
  }, [eMSNsToShow, setEMSNsToShow, eMSNs, dispatch, t])

  const viewAllEMSNs = useCallback(() => {
    setEMSNsToShow(eMSNs.length)
    resultsHeading.current?.focus()
    dispatch(logAriaMessage(t('emsn-search.search-complete')))
  }, [setEMSNsToShow, eMSNs, dispatch, t])

  if (eMSNs.length === 0) {
    return (
      <div>
        <p className='my-u-container--8 my-u-pb--4'>
          <span>{t('empty-emsn-results.text-paragraph')}</span>
          <Button href={searchClaimsUrl}>{t('empty-emsn-results.search-claims-link-text')}</Button>
        </p>
      </div>
    )
  }

  return (
    <div>
      <div className='my-u-container--8'>
        <div aria-live='polite' aria-atomic>
          <p
            ref={eMSNShownCountRef}
            className='ds-u-font-size--sm'
            tabIndex={-1}
          >
            {t('emsn-results.showing-text')} 1 - {eMSNsToShow} {t('emsn-results.of-text')} {eMSNs.length}
          </p>
        </div>
        <h2
          ref={resultsHeading}
          className='ds-u-visibility--screen-reader'
          tabIndex={-1}
        >
          {t('emsn-results.heading')}
        </h2>
        {eMSNs
          .sort(sortFunction)
          .slice(0, eMSNsToShow)
          .map((emsn) => <EMSNCard key={emsn.id} emsn={emsn} />)}
      </div>
      {eMSNsToShow < eMSNs.length && (
        <div className='my-u-pt--8'>
          <Button
            className='ds-c-button ds-c-button--solid my-u-py--6 my-u-px--16 ds-u-margin-y--2 my-u-mr--7'
            onClick={loadMoreEMSNs}
          >
            {t('load-more-button-text')}
          </Button>
          <Button
            className='ds-c-button ds-c-button--secondary my-u-py--6 my-u-px--16'
            onClick={viewAllEMSNs}
          >
            {t('show-all-button-text')}
          </Button>
        </div>
      )}
    </div>
  )
}
